import { STORES, GALLERY, SALES_REP } from '../constants';

/**
 * Formats the location details data from the api.
 *
 * @param {Object} data - Data from the response.
 * @return {Object} - Formatted data.
 */
export const formatLocationDetailsData = data => {
  let location = null;

  try {
    const {
      location_type: types,
      location_id: id,
      title,
      common_name: name,
      open_soon: upcoming,
      latitude: lat,
      longitude: lng,
      address_line_1: address1,
      address_line_2: address2,
      country,
      region,
      province_state: province,
      postal_code: postal,
      city,
      address_notes: notes,
      sales_phone: phone,
      directions_link: directions,
      hours,
      chargers,
      amenities,
      destination_website: website,
      is_gallery: gallery,
      sales_representative: salesRep,
    } = data;

    if (types && id && title && upcoming && lat && lng) {
      location = {
        types,
        id,
        name: title || name,
        commonName: name,
        upcoming: upcoming === '1',
        lat: parseFloat(lat),
        lng: parseFloat(lng),
        address1,
        address2,
        country,
        region,
        province,
        postal,
        city,
        notes,
        phone,
        directions,
        hours,
        chargers,
        amenities,
        website,
        gallery,
        salesRep,
      };
    }
  } catch (e) {
    // Error formatting location details.
  }

  return location;
};

/**
 * Returns a copy of the provided location details cache with the provided location.
 * Items in the cached are mapped by location "id".
 *
 * @param {Object} location - Location details to cache.
 * @param {Object} cache - Cache to update.
 * @return {Object} - Updated cache.
 */
export const getUpdatedLocationDetailsCache = (location, cache) => {
  const updated = { ...cache };
  updated[location.id] = location;

  return updated;
};

/**
 * Formats the address for the provided location. This function is adapted from the original
 * find us application.
 *
 * @param {Object} location - Location details data object used to build the address.
 * @return {String} - Formatted address to display to the end user.
 */
export const formatAddress = location => {
  const { commonName, address1, address2, country, region, province, postal, city } = location;

  const formatted = [];

  // Populate common name if it exists.
  if (commonName) formatted.push(commonName);

  // Populate address lines 1 and 2.
  if (address1) formatted.push(address1);
  if (address2) formatted.push(address2);

  // Countries that use only address line 1 and 2.
  const addressOnlyCountries = ['japan', 'south korea', 'taiwan'];

  if (country && addressOnlyCountries.indexOf(country.toLowerCase()) >= 0) {
    return formatted.join('\n');
  }

  // Format European address with the following format:
  // 12345 Fremont California
  // United States
  //
  // Format all other addresses with the following format:
  // Fremont, CA 12345
  let address = '';

  if (region === 'europe') {
    if (postal) address = `${postal} `;
    if (city) address = `${address} ${city}`;
    if (province) address = `${address} ${province}`;
  } else {
    if (city) address = `${city}`;
    if (province)
      address = `${address}${region === 'north_america' && city ? ',' : ''} ${province}`;
    if (postal) address = `${address} ${postal}`;
  }

  formatted.push(address.trim());

  // Add countries for certain regions.
  const countryRegions = ['europe'];
  if (countryRegions.indexOf(region) >= 0 && country) formatted.push(country);

  // Return the formatted address.
  return formatted.join('\n');
};

/**
 * Formats the provide phone string to be usable by browsers to make calls. Strips out non-number
 * characters except for "+" (to indicate international numbers).
 *
 * @param {String} phone - Phone number string.
 * @return {String} - Formatted phone number to be used for phone link.
 */
export const formatPhone = phone => {
  if (!phone) {
    return null;
  }
  return phone.replace(/[^\d+]/g, '');
};

/**
 * Gets location header type. Defaults to the location type, but overrides for special cases, such
 * as galleries and sales representative locations (rather than stores.)
 *
 * @param {String} type - Rendered location type.
 * @param {Object} location - Location data.
 * @return {Object} - Location header type.
 */
export const getLocationHeaderType = (type, location) => {
  switch (type) {
    case STORES:
      if (location.gallery) {
        return GALLERY;
      } else if (location.salesRep) {
        return SALES_REP;
      }

      break;
    default:
  }

  return type;
};
